import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import { advisorSession } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDocument } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Spinner } from '@/shared/ui/Spinner';

/**
 * Escolha da corretora do assessor (P-ACE-2): o Advisor/AuthenticationBroker do
 * OnPoint, em que o assessor "emula" uma das corretoras da assessoria.
 *
 * A sessao do assessor tem sempre escopo de corretora, e o escopo e a corretora
 * escolhida aqui. Trocar de corretora nao abre outra sessao: o servidor muda a
 * corretora desta sessao, so entre as da assessoria, e confere o vinculo a cada
 * requisicao. A troca fica nos eventos de seguranca, e cada acao na trilha leva a
 * assessoria e a corretora.
 */
export function AdvisorSessionPage() {
  const { session, switchBroker } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [trocando, setTrocando] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const corretoras = useQuery({
    queryKey: ['assessoria', 'corretoras'],
    queryFn: ({ signal }) => advisorSession.brokers(signal),
    enabled: Boolean(session?.advisor),
  });

  if (!session?.advisor) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Corretoras da assessoria" />
        <Alert tone="info">
          A escolha de corretora e do acesso da assessoria. Esta sessao nao e de uma
          assessoria.
        </Alert>
      </div>
    );
  }

  const atual =
    session.advisor.acting_broker?.id ?? corretoras.data?.acting_broker_id ?? null;

  async function atuarPor(id: string) {
    if (!switchBroker) return;
    setErro(null);
    setTrocando(id);
    try {
      await switchBroker(id);
      // O que ja foi carregado e da corretora anterior: as chaves das consultas nao
      // levam a corretora do escopo, e o cache mostraria por ate 30 s as cotacoes dela
      // com a barra ja na corretora nova (R5-M14). E o mesmo da troca de empresa.
      queryClient.clear();
      void navigate('/inicio');
    } catch (causa) {
      setErro(
        causa instanceof ApiError
          ? causa.message
          : 'Nao foi possivel trocar de corretora. Tente novamente.',
      );
    } finally {
      setTrocando(null);
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Corretoras da assessoria"
        description={`${session.advisor.name || 'Assessoria'}: escolha a corretora pela qual voce vai atuar. Tudo o que fizer fica registrado com a assessoria e a corretora.`}
      />
      {erro ? <Alert tone="danger">{erro}</Alert> : null}
      {session.advisor.master ? (
        <p className="text-xs text-ink-500">
          Usuario master da assessoria. O que a marca libera ainda nao foi definido pela
          seguradora.
        </p>
      ) : null}

      {corretoras.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : corretoras.isError ? (
        <Alert tone="danger">
          {corretoras.error instanceof ApiError
            ? corretoras.error.message
            : 'Nao foi possivel listar as corretoras da assessoria.'}
        </Alert>
      ) : corretoras.data.items.length === 0 ? (
        <EmptyState
          title="Nenhuma corretora na assessoria"
          description="A seguradora inclui as corretoras na assessoria. Sem nenhuma, nao ha por quem atuar."
        />
      ) : (
        <Card>
          <ul className="divide-y divide-line" aria-label="Corretoras da assessoria">
            {corretoras.data.items.map((corretora) => (
              <li
                key={corretora.id}
                className="flex flex-wrap items-center justify-between gap-3 px-5 py-4"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-ink-900">
                    {corretora.legal_name}
                  </p>
                  <p className="text-xs text-ink-500">
                    CNPJ {formatDocument(corretora.document)} - No {corretora.number}
                  </p>
                </div>
                {corretora.id === atual ? (
                  <Badge tone="brand">Atuando por esta corretora</Badge>
                ) : (
                  <Button
                    size="sm"
                    loading={trocando === corretora.id}
                    disabled={trocando !== null}
                    onClick={() => void atuarPor(corretora.id)}
                  >
                    Atuar por esta corretora
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
